/**
 * @author Sunil Jain
 * @description Centralized utility for making authenticated API requests, handling token refresh,
 * and consistent JSON payload serialization.
 */
import logger from './logger';

// --- The refreshToken function now lives inside this file, breaking the circular dependency ---
const refreshToken = async () => {
  const currentRefreshToken = localStorage.getItem('refreshToken');
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';

  if (!currentRefreshToken) {
    logger.error('refreshToken: No refresh token available.');
    return null;
  }

  try {
    // The endpoint here must be correct and not rely on the main authFetch logic
    const response = await fetch(`${apiBaseUrl}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: currentRefreshToken }),
    });

    if (!response.ok) {
      logger.error('refreshToken: Failed to refresh token, server responded with error.');
      // Clear all tokens on refresh failure to force re-login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user'); // Also clear any stale user data
      window.location.href = '/login'; // Force re-login
      throw new Error('Failed to refresh token');
    }

    const data = await response.json();
    localStorage.setItem('accessToken', data.access_token);
    if (data.refresh_token) {
        localStorage.setItem('refreshToken', data.refresh_token);
    }
    logger.info("refreshToken: Token refreshed and stored successfully.");
    return data.access_token;
  } catch (error) {
    logger.error('refreshToken: An exception occurred during token refresh:', error);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    return null;
  }
};


const authFetch = async (endpoint, options = {}) => {
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';

    
    const doFetch = async (token) => {
        let correctedEndpoint = endpoint;
        
        // Defensively correct the endpoint path to prevent /api/api duplication.
        // This is a common client-side error where the function is called with a full path instead of a relative one.
        if (correctedEndpoint.startsWith('/api/')) {
            correctedEndpoint = correctedEndpoint.substring(4); 
        }

        // Ensure the base URL does not have a trailing slash and the endpoint has a leading one.
        const normalizedApiBase = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
        const normalizedEndpoint = correctedEndpoint.startsWith('/') ? correctedEndpoint : `/${correctedEndpoint}`;
        
        const fullURL = `${normalizedApiBase}/api${normalizedEndpoint}`;

        // --- START: CORRECTED LOGIC ---
        // 1. Initialize headers
        const headers = { ...options.headers };

        // 2. Add Authorization token if it exists
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        // 3. Add Tenant ID if available
        try {
            const selectedTenant = JSON.parse(localStorage.getItem('selectedTenant') || 'null');
            if (selectedTenant?.tenant_id) {
                headers['X-Tenant-ID'] = selectedTenant.tenant_id;
            }
        } catch (e) {
            logger.error("[authFetch] Error processing tenant ID from localStorage:", e);
        }

        // 4. Create final fetchOptions object with fully prepared headers
        const fetchOptions = { ...options, headers };

        // 5. Prepare the body and Content-Type header.
        if (options.body) {
            if (options.body instanceof FormData) {
                // For FormData, we MUST NOT set the Content-Type header.
                // The browser needs to set it to 'multipart/form-data' with a specific boundary.
                // We explicitly delete it here to prevent any accidental headers from being sent.
                delete fetchOptions.headers['Content-Type'];
            } else if (typeof options.body === 'object') {
                // For plain objects, we stringify and set the appropriate header.
                fetchOptions.headers['Content-Type'] = 'application/json';
                fetchOptions.body = JSON.stringify(options.body);
            }
            // If body is already a string (e.g., pre-stringified JSON), we assume Content-Type was set correctly.
        }
        // FormData bodies handle their own Content-Type (multipart/form-data)

        logger.info(`[authFetch] Making request to: ${fetchOptions.method || 'GET'} ${fullURL}`);
        return fetch(fullURL, fetchOptions);
    };

    let accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
        // REASON: We are updating this list to reflect the consolidated API endpoints from Step 1.
        // The old /api/users/verify-invite is removed, and the new /api/auth/verify-invite is added.
        const publicEndpoints = [
            '/api/auth/login',
            '/api/auth/register-staff',
            '/api/auth/register-tenant',
            '/api/auth/verify-invite', // <<< MODIFIED (was /api/auth/verify-staff-invite)
            '/api/auth/reset-password',
            '/api/auth/register/patient-with-invite'
        ];
        
        const isPublicRequest = publicEndpoints.some(publicEp => endpoint.startsWith(publicEp));
        
        if (!isPublicRequest) {
            logger.error('authFetch: No access token found for a protected route. Redirecting to login.');
            window.location.href = '/login';
            throw new Error('No access token available.');
        } else {
             logger.info(`[authFetch] Making public request to ${endpoint}. No access token needed.`);
             return doFetch(null); // Proceed without a token for public routes
        }
    }


    let response = await doFetch(accessToken);

    if (response.status === 401) {
        logger.warn('authFetch: Received 401 Unauthorized. Attempting to refresh token...');
        try {
            const newAccessToken = await refreshToken();
            if (newAccessToken) {
                logger.info('authFetch: Token refreshed successfully. Retrying the original request.');
                response = await doFetch(newAccessToken);
            } else {
                logger.error('authFetch: Could not refresh token. Redirecting to login.');
                // No need to redirect here, refreshToken() already does it on failure
                throw new Error("Authentication failed"); 
            }
        } catch (error) {
            logger.error('authFetch: An error occurred during token refresh chain:', error);
            throw error;
        }
    }

    if (!response.ok) {
        if (response.status === 401) {
            // If we still have a 401 after a refresh attempt, it's a definitive failure.
            // Throw the specific error that AuthContext is designed to catch.
            logger.error('authFetch: Request failed with 401 even after token refresh attempt.');
            throw new Error("Authentication failed");
        }
        let errorData;
        try { 
            errorData = await response.json(); 
        } catch (e) {
             const error = new Error(`API call failed: ${response.status} ${response.statusText}`);
             error.status = response.status;
             throw error;
        }
        // Use the detailed message from the backend if available
        const message = errorData.detail?.[0]?.msg || errorData.detail || `API error: ${response.status}`;
        const error = new Error(message);
        error.data = errorData;
        error.status = response.status;
        throw error;
    }
    
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
        const text = await response.text();
        return text ? JSON.parse(text) : {};
    } else {
        if (response.status === 204) {
            return {}; 
        }
        return response.blob(); 
    }
};

export default authFetch;